import React, { useCallback, useContext, useState, MouseEventHandler, useEffect } from 'react'
import { OverlayTrigger, Popover, Dropdown } from 'react-bootstrap'
import { AuthContext } from '../context'
import { Trans } from 'react-i18next'
import { SigninLink } from './auth-link'
import { ButtonVariant } from 'app/modules/shared/components/types'
import Button from 'app/modules/shared/components/button'

export interface AuthButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  itemSelect?: (key: string | null) => void
  groupLabel?: string | JSX.Element
  delay?: number
  onClick?: MouseEventHandler<HTMLElement>
  variant?: ButtonVariant
  isPrivate: boolean
  privateMsg: string
  fileSize?: string | number
  sizeMsg?: string
  className?: string
  disabled?: boolean
  children?: JSX.Element | string | (string | JSX.Element)[]
}
export function AuthButton(props: AuthButtonProps): React.ReactElement {
  const {
    itemSelect,
    groupLabel,
    delay,
    onClick,
    variant,
    style,
    isPrivate,
    privateMsg,
    fileSize,
    sizeMsg,
    disabled,
    className,
  } = props
  const [show, setShow] = useState(false)
  const [triggeredState, setTriggeredState] = useState(false)
  const [triggeredStep, setTriggeredStep] = useState<Date>()
  const user = useContext(AuthContext)?.user

  const signinMsg = (
    <Trans i18nKey="sign-in-first">
      You need to <SigninLink>sign in</SigninLink> first.
    </Trans>
  )

  const handleToggle = useCallback((doShow: boolean) => {
    setTriggeredState(doShow)
    setTriggeredStep(new Date())
  }, [])

  // Open or show popup depending on user click on button.
  // Auto hide popup after some timeout after showing.
  useEffect(() => {
    // Use timeout to avoid closing popup immediately due to outside click hindler
    setTimeout(() => {
      setShow(triggeredState)
    }, 10)

    let closeTimeout: NodeJS.Timeout
    if (triggeredState) {
      closeTimeout = setTimeout(() => {
        setShow(false)
      }, delay ?? 2000)
    }

    return () => closeTimeout && clearTimeout(closeTimeout)
  }, [delay, triggeredState, triggeredStep])

  // Close popup on outside click
  useEffect(() => {
    function action() {
      show && setShow(false)
    }

    document.body.addEventListener('click', action)
    return () => document.body.removeEventListener('click', action)
  }, [show])

  const isAuthorized = user && !isPrivate && (fileSize || !sizeMsg)
  const isDisabled = !isAuthorized ? true : disabled

  // This container is needed to trigger click even on disabled button
  const button = itemSelect ? (
    <Dropdown className={className} style={style as any} onSelect={!isAuthorized ? undefined : itemSelect}>
      <Dropdown.Toggle variant={variant || 'tertiary'} disabled={isDisabled} className="w-100">
        {groupLabel}
      </Dropdown.Toggle>
      {props.children}
    </Dropdown>
  ) : (
    <Button
      variant={variant}
      className={className}
      onClick={!isAuthorized ? undefined : onClick}
      style={style as any}
      disabled={isDisabled}
    >
      {props.children}
    </Button>
  )

  if (!isAuthorized) {
    const msg = !user ? signinMsg : isPrivate ? privateMsg : sizeMsg

    return (
      <OverlayTrigger
        trigger="click"
        placement="auto"
        onToggle={handleToggle}
        show={show}
        overlay={
          <Popover id="popover-basic">
            <Popover.Body>{msg}</Popover.Body>
          </Popover>
        }
      >
        <div className="d-inline-block" style={{ cursor: 'pointer' }}>
          {button}
        </div>
      </OverlayTrigger>
    )
  }

  return button
}
